import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@capacit-com/lara-law-shared-ui";
import {
  Button,
  Checkbox,
  Input,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormDialog,
  FormDialogCancel,
  Textarea,
} from "@capacit-com/lara-law-shared-ui";
import { PermissionGate } from "@/lib/permissions/PermissionGate";
import { Permission } from "@/lib/permissions/Permission.enum";
import { Search, Send, Settings, Trash2 } from "lucide-react";
import { FC, useEffect, useMemo, useState } from "react";
import useFeatureStore from "@/stores/feature.store";
import useGroups from "@/hooks/useGroups";
import useUsers from "@/hooks/useUsers";
import { Group } from "@/models/feature/feature";
import CreateAndEditGroupDialog from "@/pages/settings/features/components/CreateAndEditGroupDialog";
import { ConfirmationDialog } from "@capacit-com/lara-law-shared-ui";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";
import { useUserManagementContext } from "@/providers/user-management/UserManagementContext";
import { SortableTableHeader } from "./SortableTableHeader";
import { GROUPS_LAYOUT } from "../utils/tableColumnWidths";
import {
  UserManagementSkeletonRows,
  UserManagementTableHeader,
} from "./UserManagementTableColumns";
import {
  maxColumnSpan,
  type UserManagementColumn,
} from "../utils/userManagementColumns";
import { sortGroups } from "../utils/sortGroups";
import UserManagementTableActions from "./UserManagementTableActions";
import { UserManagementTabs } from "../utils/userManagementTabs";

const GroupsTable: FC = () => {
  const {
    groupsSearch,
    setGroupsSearch,
    groupsSortBy,
    groupsSortAscending,
    setGroupsSort,
    bulkMode,
    setBulkMode,
    openCreateGroupDialog,
    setOpenCreateGroupDialog,
  } = useUserManagementContext();
  const { groups, setGroups } = useFeatureStore();
  const { useGetGroups, remove, sendMessage } = useGroups();
  const { useQueryAllTenantUsers } = useUsers();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<Group | null>(null);
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
  const [searchInputValue, setSearchInputValue] = useState(groupsSearch);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [sendMessageDialogOpen, setSendMessageDialogOpen] = useState(false);
  const [groupToSendMessage, setGroupToSendMessage] = useState<Group | null>(
    null
  );
  const [sendMessageBody, setSendMessageBody] = useState("");

  const fetchedGroups = useGetGroups();
  const fetchedUsers = useQueryAllTenantUsers();

  useEffect(() => {
    if (fetchedGroups.isSuccess && fetchedGroups.data) {
      setGroups(fetchedGroups.data);
    }
  }, [fetchedGroups.data, fetchedGroups.isSuccess, setGroups]);

  // The page header owns the bulk-mode flag; the selection it applies to is ours to clear.
  useEffect(() => {
    setSelectedGroups(new Set());
  }, [bulkMode]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setGroupsSearch(searchInputValue.trim());
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchInputValue, setGroupsSearch]);

  const filteredGroups = useMemo(() => {
    const searchLower = groupsSearch.trim().toLowerCase();
    const matching = searchLower
      ? groups.filter((g) => g.name.toLowerCase().includes(searchLower))
      : groups;
    return sortGroups(matching, groupsSortBy, groupsSortAscending);
  }, [groups, groupsSearch, groupsSortBy, groupsSortAscending]);

  const validateGroupName = (name: string, groupId?: string) => {
    if (name.length < 3) {
      showErrorToast("Gruppenavnet skal være mindst 3 tegn");
      return false;
    }
    if (groups.some((g) => g.name === name && g.id !== groupId)) {
      showErrorToast("Gruppenavnet eksisterer allerede");
      return false;
    }
    return true;
  };

  const handleRowClick = (group: Group) => {
    setSelectedGroup(group);
    setEditDialogOpen(true);
  };

  const handleSettingsClick = (e: React.MouseEvent, group: Group) => {
    e.stopPropagation();
    setSelectedGroup(group);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (e: React.MouseEvent, group: Group) => {
    e.stopPropagation();
    setGroupToDelete(group);
    setDeleteDialogOpen(true);
  };

  const handleSendMessageClick = (e: React.MouseEvent, group: Group) => {
    e.stopPropagation();
    setGroupToSendMessage(group);
    setSendMessageBody("");
    setSendMessageDialogOpen(true);
  };

  const handleSendMessageSubmit = async () => {
    const message = sendMessageBody.trim();
    if (!message || !groupToSendMessage) return;
    try {
      await sendMessage.mutateAsync({
        groupId: groupToSendMessage.id,
        body: { message },
      });
      showSuccessToast("Besked sendt til gruppen");
      setSendMessageDialogOpen(false);
      setGroupToSendMessage(null);
      setSendMessageBody("");
    } catch (error) {
      logger.error("Failed to send message to group", {
        operation: "sendGroupMessage",
        error,
        groupId: groupToSendMessage.id,
      });
      showErrorToast(
        error instanceof Error ? error.message : "Beskeden kunne ikke sendes"
      );
    }
  };

  const handleSendMessageDialogClose = (open: boolean) => {
    if (!open) {
      setSendMessageDialogOpen(false);
      setGroupToSendMessage(null);
      setSendMessageBody("");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!groupToDelete) return;
    try {
      await remove.mutateAsync(groupToDelete.id);
      showSuccessToast("Gruppe slettet");
      setDeleteDialogOpen(false);
      setGroupToDelete(null);
    } catch (error) {
      logger.error("Failed to delete group", {
        operation: "deleteGroup",
        error,
        groupId: groupToDelete.id,
      });
      showErrorToast("Gruppen kunne ikke slettes");
    }
  };

  const handleEditDialogClose = (open: boolean) => {
    setEditDialogOpen(open);
    if (!open) setSelectedGroup(null);
  };

  const handleBulkSelect = (groupId: string, checked: boolean) => {
    const next = new Set(selectedGroups);
    if (checked) next.add(groupId);
    else next.delete(groupId);
    setSelectedGroups(next);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedGroups(new Set(filteredGroups.map((g) => g.id)));
    } else {
      setSelectedGroups(new Set());
    }
  };

  const handleBulkDeleteConfirm = async () => {
    if (selectedGroups.size === 0) return;
    setIsBulkDeleting(true);
    try {
      for (const id of selectedGroups) {
        await remove.mutateAsync(id);
      }
      showSuccessToast(
        `${selectedGroups.size} gruppe${selectedGroups.size !== 1 ? "r" : ""} slettet`
      );
      setBulkDeleteDialogOpen(false);
      setSelectedGroups(new Set());
      setBulkMode(false);
    } catch (error) {
      logger.error("Failed to delete selected groups", {
        operation: "bulkDeleteGroups",
        error,
        groupCount: selectedGroups.size,
      });
      showErrorToast("En eller flere grupper kunne ikke slettes");
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleSearch = () => {
    setGroupsSearch(searchInputValue.trim());
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearch();
  };

  const isLoading =
    fetchedGroups.isLoading ||
    fetchedGroups.isPending ||
    fetchedUsers.isLoading ||
    fetchedUsers.isPending;

  const columns: UserManagementColumn[] = [
    {
      key: "select",
      width: GROUPS_LAYOUT.columns.select,
      hidden: !bulkMode,
      permission: [Permission.UpdateFeatureGroups],
      header: (
        <Checkbox
          checked={
            filteredGroups.length > 0 &&
            selectedGroups.size === filteredGroups.length
          }
          onCheckedChange={handleSelectAll}
        />
      ),
      skeleton: <Skeleton className="size-4" />,
    },
    {
      key: "name",
      width: GROUPS_LAYOUT.columns.name,
      header: (
        <SortableTableHeader
          label="Gruppenavn"
          sortBy="name"
          activeSortBy={groupsSortBy}
          ascending={groupsSortAscending}
          onSort={setGroupsSort}
        />
      ),
      skeleton: <Skeleton className="h-4 w-full max-w-64" />,
    },
    {
      key: "memberCount",
      width: GROUPS_LAYOUT.columns.memberCount,
      header: (
        <SortableTableHeader
          label="Antal medlemmer"
          sortBy="memberCount"
          activeSortBy={groupsSortBy}
          ascending={groupsSortAscending}
          onSort={setGroupsSort}
        />
      ),
      skeleton: <Skeleton className="h-4 w-full max-w-16" />,
    },
    {
      key: "actions",
      width: GROUPS_LAYOUT.columns.actions,
      header: "Handlinger",
      skeleton: <Skeleton className="h-8 w-full max-w-[120px]" />,
    },
  ];
  const colSpan = maxColumnSpan(columns);

  return (
    <div className="flex flex-col h-full gap-4 min-h-0">
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="mb-4 flex items-center justify-between gap-4 min-h-9">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Søg efter grupper..."
              className="pl-8 pr-10"
              value={searchInputValue}
              onChange={(e) => setSearchInputValue(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            <Button
              size="icon"
              variant="ghost"
              className="absolute right-0 top-0 h-full px-2"
              onClick={handleSearch}
              disabled={isLoading}
            >
              <Search className="h-4 w-4" />
            </Button>
          </div>
          <UserManagementTableActions tab={UserManagementTabs.GROUPS} />
        </div>
        {bulkMode && (
          <div
            className={cn(
              "flex items-center justify-between p-3 bg-primary/10 mb-4 rounded-md",
              selectedGroups.size === 0 && "opacity-50"
            )}
          >
            <span className="text-sm font-medium">
              {selectedGroups.size} gruppe{selectedGroups.size !== 1 ? "r" : ""}{" "}
              valgt
            </span>
            <Button
              disabled={selectedGroups.size === 0}
              size="sm"
              variant="outline"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => setBulkDeleteDialogOpen(true)}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Slet valgte grupper
            </Button>
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-y-auto border rounded-xs">
          <Table
            removeOverflowX
            className="table-fixed"
            style={{ minWidth: GROUPS_LAYOUT.minWidth }}
          >
            <UserManagementTableHeader columns={columns} />
            <TableBody>
              {isLoading ? (
                <UserManagementSkeletonRows columns={columns} rows={5} />
              ) : filteredGroups.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={colSpan}
                    className="h-24 text-center text-muted-foreground text-sm"
                  >
                    {groupsSearch
                      ? "Ingen grupper matcher søgningen"
                      : "Ingen grupper fundet"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredGroups.map((group) => (
                  <TableRow
                    key={group.id}
                    className={cn(
                      "group cursor-pointer hover:bg-muted/50",
                      bulkMode && "cursor-default"
                    )}
                    onClick={() => !bulkMode && handleRowClick(group)}
                  >
                    {bulkMode && (
                      <PermissionGate allOf={[Permission.UpdateFeatureGroups]}>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedGroups.has(group.id)}
                            onCheckedChange={(checked) =>
                              handleBulkSelect(group.id, checked as boolean)
                            }
                          />
                        </TableCell>
                      </PermissionGate>
                    )}
                    <TableCell className="truncate">
                      <span className="font-medium">{group.name}</span>
                    </TableCell>
                    <TableCell>{group.userIds.length}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-0">
                        <PermissionGate
                          allOf={[Permission.UpdateFeatureGroups]}
                        >
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={(e) => handleSendMessageClick(e, group)}
                            className="shrink-0"
                            disabled={sendMessage.isPending || bulkMode}
                            aria-label="Send besked til gruppe"
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                        </PermissionGate>
                        <PermissionGate
                          allOf={[Permission.UpdateFeatureGroups]}
                        >
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={(e) => handleSettingsClick(e, group)}
                            className="shrink-0"
                            disabled={remove.isPending || bulkMode}
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                        </PermissionGate>
                        <PermissionGate
                          allOf={[Permission.UpdateFeatureGroups]}
                        >
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={(e) => handleDeleteClick(e, group)}
                            className="shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                            disabled={remove.isPending || bulkMode}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </PermissionGate>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Send message dialog */}
      <FormDialog
        open={sendMessageDialogOpen}
        onOpenChange={handleSendMessageDialogClose}
        isDirty={sendMessageBody.trim() !== ""}
      >
        <DialogHeader>
          <DialogTitle>Send besked til {groupToSendMessage?.name}</DialogTitle>
          <DialogDescription>
            Alle medlemmer af gruppen modtager beskeden som en notifikation.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-2">
            <label htmlFor="send-message-body" className="text-sm font-medium">
              Besked <span className="text-destructive">*</span>
            </label>
            <Textarea
              id="send-message-body"
              placeholder="Skriv beskeden til gruppen..."
              value={sendMessageBody}
              onChange={(e) => setSendMessageBody(e.target.value)}
              rows={4}
              className="resize-none"
            />
          </div>
        </div>
        <DialogFooter>
          <FormDialogCancel disabled={sendMessage.isPending} />
          <Button
            onClick={handleSendMessageSubmit}
            disabled={!sendMessageBody.trim() || sendMessage.isPending}
          >
            <Send className="w-4 h-4 mr-1" />
            {sendMessage.isPending ? "Sender..." : "Send besked"}
          </Button>
        </DialogFooter>
      </FormDialog>

      {/* Create dialog (controlled, opened from the page header) */}
      <PermissionGate allOf={[Permission.CreateFeatureGroups]}>
        <CreateAndEditGroupDialog
          open={openCreateGroupDialog}
          onOpenChange={setOpenCreateGroupDialog}
          validateGroupName={validateGroupName}
        />
      </PermissionGate>

      {/* Edit dialog (controlled, opened from row click or gear) */}
      <CreateAndEditGroupDialog
        open={editDialogOpen}
        onOpenChange={handleEditDialogClose}
        group={selectedGroup ?? undefined}
        validateGroupName={validateGroupName}
      />

      <ConfirmationDialog
        title={`Slettning af gruppen: ${groupToDelete?.name}`}
        description={`Du er i gang med at slette gruppen ${groupToDelete?.name}, er du sikker på du vil fortsætte?\n\nVed at slette denne gruppe, vil alle medlemmer af denne gruppe miste adgang til de features hvor gruppen er tilskrevet.`}
        onConfirm={handleDeleteConfirm}
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setGroupToDelete(null);
        }}
        confirmText="Slet"
        confirmIcon={<Trash2 className="w-4 h-4" />}
        variant="destructive"
        confirmPending={remove.isPending}
      />

      <ConfirmationDialog
        title="Slet valgte grupper"
        description={`Er du sikker på, at du vil slette ${selectedGroups.size} gruppe${selectedGroups.size !== 1 ? "r" : ""}?\n\nAlle medlemmer af disse grupper vil miste adgang til de features hvor grupperne er tilskrevet.`}
        onConfirm={handleBulkDeleteConfirm}
        isOpen={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        confirmText="Slet grupper"
        confirmIcon={<Trash2 className="w-4 h-4" />}
        variant="destructive"
        confirmPending={isBulkDeleting}
      />
    </div>
  );
};

export default GroupsTable;
